import { useQuery, useMutation, useQueryClient } from 'react-query';
import { clientsApi } from '../services/api/clients';
import { useToast } from './useToast';
import type { ClientCreate, ClientUpdate } from '../types/client';
import type { ClientListParams } from '../services/api/clients';

export function useClients(params?: ClientListParams) {
  return useQuery(['clients', params], () => clientsApi.list(params));
}

export function useClient(clientId: string) {
  return useQuery(['clients', clientId], () => clientsApi.getById(clientId), {
    enabled: !!clientId,
  });
}

export function useCreateClient() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation((data: ClientCreate) => clientsApi.create(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(['clients']);
      showSuccess('Cliente creado exitosamente');
    },
    onError: (error) => {
      showError(error, 'Error al crear el cliente');
    },
  });
}

export function useUpdateClient() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation(
    ({ clientId, data }: { clientId: string; data: ClientUpdate }) =>
      clientsApi.update(clientId, data),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(['clients']);
        queryClient.invalidateQueries(['clients', variables.clientId]);
        showSuccess('Cliente actualizado exitosamente');
      },
      onError: (error) => {
        showError(error, 'Error al actualizar el cliente');
      },
    }
  );
}

export function useDeactivateClient() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation(
    ({ clientId, confirmDeactivateWithProjects }: { clientId: string; confirmDeactivateWithProjects?: boolean }) =>
      clientsApi.deactivate(clientId, confirmDeactivateWithProjects),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['clients']);
        showSuccess('Cliente desactivado exitosamente');
      },
      onError: (error) => {
        showError(error, 'Error al desactivar el cliente');
      },
    }
  );
}

export function useReactivateClient() {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation(
    (clientId: string) => clientsApi.reactivate(clientId),
    {
      onSuccess: (_data, clientId) => {
        queryClient.invalidateQueries(['clients']);
        queryClient.invalidateQueries(['clients', clientId]);
        showSuccess('Cliente reactivado exitosamente');
      },
      onError: (error) => {
        showError(error, 'Error al reactivar el cliente');
      },
    }
  );
}
